import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button } from '../catalyst/button'
import { Checkbox } from '../catalyst/checkbox'
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../catalyst/dialog'
import { Field as FormField, FieldGroup, Label } from '../catalyst/fieldset'
import { Input } from '../catalyst/input'
import { Text } from '../catalyst/text'
import { useAuth } from '../lib/auth'
import { issueFor, useCanadaGapFarmStatus, useEntryCheck } from '../lib/canadagap'
import { ComplianceBadge } from '../lib/compliance-badge'
import { useVisitorEntries, type VisitorEntryInput } from '../lib/farm-level-logs'
import { byDateDesc, FarmLogLayout, orNull, text, useSavedEntries, type DialogState } from '../lib/farm-log-layout'
import type { VisitorEntry } from '../lib/types'
import { checkBadgeProps, EntryCheckPanel, InputWarning } from './entry-check'
import { EntryIssues } from './entry-issues'

const FORM_NAME = 'Visitor Sign-In Log'

/** The visitor policy as printed on Form L. */
const VISITOR_POLICY = [
  'Remain in the area they are given permission to be in (e.g. a contractor remains in the work area only)',
  'Not enter controlled-access areas with a disease or illness transferable to food, its symptoms, or an open or infected lesion',
  'Wash hands before entering controlled-access areas',
  'Not handle product or materials unless given permission',
  'Wear appropriate protective and/or food safety-related clothing; clean, change or cover visibly dirty or soiled shoes',
]

function VisitorEntryDialog({ onClose, initial, onSave }: { onClose: () => void; initial?: VisitorEntry; onSave: (input: VisitorEntryInput) => Promise<unknown> }) {
  const [visitedOn, setVisitedOn] = useState(initial?.visitedOn ?? '')
  const [visitorName, setVisitorName] = useState(initial?.visitorName ?? '')
  const [companyName, setCompanyName] = useState(text(initial?.companyName))
  const [purpose, setPurpose] = useState(text(initial?.purpose))
  const [location, setLocation] = useState(text(initial?.location))
  const [policyAcknowledged, setPolicyAcknowledged] = useState(initial?.policyAcknowledged ?? false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { org } = useAuth()
  const { farmId } = useParams<{ farmId: string }>()
  const check = useEntryCheck(org?.id, farmId, { form: 'l', entry: { visitedOn: visitedOn || null, visitorName: orNull(visitorName), companyName: orNull(companyName), purpose: orNull(purpose), location: orNull(location), policyAcknowledged } })

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!visitedOn || !visitorName.trim()) return
    setSubmitting(true)
    setError(null)
    try {
      await onSave({ visitedOn, visitorName: visitorName.trim(), companyName: orNull(companyName), purpose: orNull(purpose), location: orNull(location), policyAcknowledged })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save visitor.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>{initial ? 'Edit visitor sign-in' : 'Sign in a visitor'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <ComplianceBadge framework="CanadaGAP" formCode="L" formName={FORM_NAME} {...checkBadgeProps(check)} />
        </div>
        <DialogBody>
          {error && <Text className="mb-4 text-red-600 dark:text-red-400">{error}</Text>}
          <FieldGroup>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField>
                <Label>Date</Label>
                <Input type="date" value={visitedOn} onChange={(e) => setVisitedOn(e.target.value)} required autoFocus />
              </FormField>
              <FormField>
                <Label>Visitor's name</Label>
                <Input value={visitorName} onChange={(e) => setVisitorName(e.target.value)} required />
              </FormField>
            </div>
            <FormField>
              <Label>Company name</Label>
              <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
            </FormField>
            <FormField>
              <Label>Purpose of visit</Label>
              <Input value={purpose} onChange={(e) => setPurpose(e.target.value)} />
            </FormField>
            <FormField>
              <Label>Location on premises</Label>
              <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Where they are permitted to be" />
            </FormField>
          </FieldGroup>

          <div className="mt-8 rounded-lg bg-zinc-50 p-4 ring-1 ring-zinc-950/5 dark:bg-white/5 dark:ring-white/10">
            <p className="text-sm font-medium text-zinc-950 dark:text-white">Visitor policy — all visitors must:</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {VISITOR_POLICY.map((rule) => (
                <li key={rule} className="text-sm text-zinc-600 dark:text-zinc-400">
                  {rule}
                </li>
              ))}
            </ul>
            <label className="mt-4 flex items-start gap-3">
              <Checkbox checked={policyAcknowledged} onChange={setPolicyAcknowledged} className="mt-0.5" />
              <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">The visitor was informed of and understands the visitor policy</span>
            </label>
          </div>
          <EntryCheckPanel result={check} formCode="L" />
        </DialogBody>
        <DialogActions>
          <div className="sm:mr-auto">
            <ComplianceBadge framework="CanadaGAP" formCode="L" formName={FORM_NAME} placement="above" {...checkBadgeProps(check)} />
          </div>
          <Button plain onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export function VisitorEntriesPage() {
  const { farmId } = useParams<{ farmId: string }>()
  const { org } = useAuth()
  const [year, setYear] = useState(new Date().getFullYear())
  const [dialog, setDialog] = useState<DialogState<VisitorEntry>>(null)
  const saved = useSavedEntries(dialog !== null, year)

  const { logs, loading, error, addLog, updateLog } = useVisitorEntries(org?.id, farmId, year)
  const { status, reload: reloadStatus } = useCanadaGapFarmStatus(org?.id, farmId, year)

  async function save(input: VisitorEntryInput) {
    const log = dialog?.log ? await updateLog(dialog.log.id, input) : await addLog(input)
    await reloadStatus()
    if (log) saved.setIds([log.id])
  }

  const entries = [...logs].sort(byDateDesc((r) => r.visitedOn)).map((log) => ({
    key: log.id,
    date: log.visitedOn,
    onClick: () => setDialog({ log }),
    content: (
      <>
        <p className="mt-0.5 text-sm text-zinc-700 dark:text-zinc-300">
          {log.visitorName}
          {log.companyName && <span className="text-zinc-400 dark:text-zinc-500"> · {log.companyName}</span>}
        </p>
        {(log.purpose || log.location) && (
          <p className="mt-0.5 text-sm text-zinc-500 dark:text-zinc-400">{[log.purpose, log.location].filter(Boolean).join(' · ')}</p>
        )}
        <EntryIssues issue={issueFor(status?.forms.l, log.id)} />
      </>
    ),
  }))

  return (
    <FarmLogLayout
      orgId={org?.id}
      farmId={farmId}
      form="l"
      title="Visitor sign-in"
      formName={FORM_NAME}
      addLabel="Sign in a visitor"
      addHref={`/farms/${farmId}/log/visitor`}
      year={year}
      onYearChange={setYear}
      status={status}
      loading={loading}
      error={error}
      entries={entries}
      emptyText={`No visitors in ${year}.`}
      saved={saved}
    >
      {dialog && <VisitorEntryDialog key={dialog.log?.id ?? 'new'} onClose={() => setDialog(null)} initial={dialog.log} onSave={save} />}
    </FarmLogLayout>
  )
}
